import { useEffect, useState, useCallback } from "react";
import { Search, Plus, Pencil, Trash2, Loader2, Building2, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const EMPTY_OWNER = { nom: "", prenom: "", adresse: "", telephone: "", profession: "", notes: "" };

export function OwnerForm({ value, onChange, prefix }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-nom`}>Nom *</Label>
        <Input id={`${prefix}-nom`} value={value.nom} onChange={set("nom")} placeholder="Ex : DIABY" data-testid={`${prefix}-nom`} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-prenom`}>Prénom *</Label>
        <Input id={`${prefix}-prenom`} value={value.prenom} onChange={set("prenom")} placeholder="Ex : Moctar" data-testid={`${prefix}-prenom`} />
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor={`${prefix}-adresse`}>Adresse</Label>
        <Input id={`${prefix}-adresse`} value={value.adresse} onChange={set("adresse")} placeholder="Ex : Bamako, Hippodrome" data-testid={`${prefix}-adresse`} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-telephone`}>Téléphone</Label>
        <Input id={`${prefix}-telephone`} value={value.telephone} onChange={set("telephone")} placeholder="Ex : 66 00 11 22" data-testid={`${prefix}-telephone`} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-profession`}>Profession (si nécessaire)</Label>
        <Input id={`${prefix}-profession`} value={value.profession} onChange={set("profession")} placeholder="Ex : Entrepreneur" data-testid={`${prefix}-profession`} />
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor={`${prefix}-notes`}>Autres informations utiles</Label>
        <Input id={`${prefix}-notes`} value={value.notes} onChange={set("notes")} placeholder="Ex : Propriétaire de la Maison A" data-testid={`${prefix}-notes`} />
      </div>
    </div>
  );
}

export default function Owners() {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(null); // {mode: 'create'|'edit', owner?}
  const [form, setForm] = useState(EMPTY_OWNER);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [contractsOf, setContractsOf] = useState(null); // {owner, contracts, loading}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/owners", { params: { search } });
      setOwners(data);
    } catch (e) {
      toast.error(formatApiError(e, "Impossible de charger les propriétaires"));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => { setForm(EMPTY_OWNER); setDialog({ mode: "create" }); };
  const openEdit = (o) => {
    setForm({ nom: o.nom, prenom: o.prenom, adresse: o.adresse || "", telephone: o.telephone || "", profession: o.profession || "", notes: o.notes || "" });
    setDialog({ mode: "edit", owner: o });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (dialog.mode === "create") {
        await api.post("/owners", form);
        toast.success("Propriétaire ajouté");
      } else {
        await api.patch(`/owners/${dialog.owner.owner_id}`, form);
        toast.success("Propriétaire mis à jour");
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Enregistrement impossible"));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/owners/${deleting.owner_id}`);
      toast.success(`Propriétaire ${deleting.prenom} ${deleting.nom} supprimé`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Suppression impossible"));
    }
  };

  const showContracts = async (o) => {
    setContractsOf({ owner: o, contracts: [], loading: true });
    try {
      const { data } = await api.get(`/owners/${o.owner_id}/contracts`);
      setContractsOf({ owner: o, contracts: data, loading: false });
    } catch {
      setContractsOf({ owner: o, contracts: [], loading: false });
    }
  };

  return (
    <div className="space-y-6" data-testid="owners-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <Building2 className="h-7 w-7 text-slate-700" /> Propriétaires
          </h1>
          <p className="text-sm text-slate-500 mt-1">Gérez les propriétaires des biens mis en location.</p>
        </div>
        <Button onClick={openCreate} data-testid="owners-add-btn">
          <Plus className="h-4 w-4 mr-2" /> Ajouter un propriétaire
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Rechercher un propriétaire (nom, prénom, adresse, téléphone)…"
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="owners-search-input" />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="owners-loading">
            <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
          </div>
        ) : owners.length === 0 ? (
          <p className="py-16 text-center text-sm text-slate-500" data-testid="owners-empty">
            Aucun propriétaire. Cliquez sur « Ajouter un propriétaire » pour commencer.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="owners-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left">
                  <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Propriétaire</th>
                  <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Adresse</th>
                  <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Téléphone</th>
                  <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Contrats</th>
                  <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {owners.map((o) => (
                  <tr key={o.owner_id} className="hover:bg-slate-50 transition-colors duration-100" data-testid={`owner-row-${o.owner_id}`}>
                    <td className="px-5 py-3.5">
                      <p className="font-semibold text-slate-900">{o.prenom} {o.nom.toUpperCase()}</p>
                      {o.profession && <p className="text-xs text-slate-500">{o.profession}</p>}
                    </td>
                    <td className="px-5 py-3.5 text-slate-600">{o.adresse || "—"}</td>
                    <td className="px-5 py-3.5 text-slate-600 whitespace-nowrap">{o.telephone || "—"}</td>
                    <td className="px-5 py-3.5">
                      <button onClick={() => showContracts(o)} data-testid={`owner-contracts-${o.owner_id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors duration-150">
                        <FileText className="h-3 w-3" /> {o.contracts_count} contrat{o.contracts_count > 1 ? "s" : ""}
                      </button>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button title="Modifier" onClick={() => openEdit(o)} data-testid={`owner-edit-${o.owner_id}`}
                          className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors duration-150">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button title="Supprimer" onClick={() => setDeleting(o)} data-testid={`owner-delete-${o.owner_id}`}
                          className="p-2 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors duration-150">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-2xl" data-testid="owner-form-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "create" ? "Nouveau propriétaire" : "Modifier le propriétaire"}</DialogTitle>
          </DialogHeader>
          <OwnerForm value={form} onChange={setForm} prefix="owner-input" />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setDialog(null)} data-testid="owner-form-cancel-btn">Annuler</Button>
            <Button onClick={save} disabled={saving || !form.nom.trim() || !form.prenom.trim()} data-testid="owner-form-save-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {dialog?.mode === "create" ? "Ajouter" : "Enregistrer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!contractsOf} onOpenChange={(o) => !o && setContractsOf(null)}>
        <DialogContent className="max-w-2xl" data-testid="owner-contracts-dialog">
          <DialogHeader>
            <DialogTitle>Contrats — {contractsOf?.owner.prenom} {contractsOf?.owner.nom.toUpperCase()}</DialogTitle>
          </DialogHeader>
          {contractsOf?.loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
          ) : contractsOf?.contracts.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">Aucun contrat associé à ce propriétaire.</p>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-96 overflow-auto">
              {contractsOf?.contracts.map((c) => (
                <li key={c.contract_id} className="py-3 flex items-center justify-between gap-3" data-testid={`owner-contract-row-${c.contract_id}`}>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{c.nom_complet}</p>
                    <p className="text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString("fr-FR")}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                    c.type === "professionnel" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"
                  }`}>
                    {c.type === "professionnel" ? "Professionnel" : "Personnel"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="owner-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce propriétaire ?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleting?.prenom} {deleting?.nom.toUpperCase()}</strong> sera définitivement supprimé.
              {deleting?.contracts_count > 0 && ` Attention : ${deleting.contracts_count} contrat(s) y sont liés.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="owner-delete-cancel-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="owner-delete-confirm-btn">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
